import React, { Fragment } from 'react';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useModal } from 'react-native-modalfy';
import { useNavigation } from '@react-navigation/native';

import {
  BaseText,
  Icon,
  Separator,
  Touchable,
  Popover,
  IconTouchable,
  PopoverPositionEnum,
  TextTouchable,
  ButtonStyleEnum,
} from '@components';
import { TrackFeedbackStatusEnum } from '@api';
import type { TrackFeedback } from '@models/Tracks/TrackFeedbackModel';
import { ShareService } from '@services';
import { ColorMapType } from '@types';
import { modals, ModalStackParamsType } from '@modals';
import { IS_WEB } from '@utils';
import { usePopover } from '@hooks';
import { screens, TabNavigatorNavigationPropType } from '@navigation';

interface IStatusStylesProps {
  status: TrackFeedbackStatusEnum;
}

const statusLocalKeyMap: Record<TrackFeedbackStatusEnum, string> = {
  [TrackFeedbackStatusEnum.New]: 'new',
  [TrackFeedbackStatusEnum.Reviewed]: 'reviewed',
  [TrackFeedbackStatusEnum.UnableToReview]: 'unableToReview',
};

const statusBlockLocalColorMap: ColorMapType<TrackFeedbackStatusEnum> =
  {
    [TrackFeedbackStatusEnum.New]: '$info80',
    [TrackFeedbackStatusEnum.Reviewed]: '$primary80',
    [TrackFeedbackStatusEnum.UnableToReview]: '$error80',
  };

const statusTextLocalColorMap: ColorMapType<TrackFeedbackStatusEnum> =
  {
    [TrackFeedbackStatusEnum.New]: '$info400',
    [TrackFeedbackStatusEnum.Reviewed]: '$primary400',
    [TrackFeedbackStatusEnum.UnableToReview]: '$error400',
  };

const Container = styled(View)({
  alignItems: 'center',
  justifyContent: [null, 'center'],
  padding: '$8',
  bg: '$background10',
  flexDirection: 'row',
  width: '100%',
  borderRadius: 4,
});

const HelpingBlock = styled(View)({
  height: 32,
  justifyContent: 'center',
});

const StatusBlock = styled(View)((props: IStatusStylesProps) => ({
  height: 32,
  borderRadius: 4,
  backgroundColor: statusBlockLocalColorMap[props.status],
  alignItems: 'center',
  justifyContent: 'center',
  px: '$3',
  flexDirection: 'row',
}));

const SuggestionContainer = styled(View)({
  maxWidth: 364,
  p: '$6',
  bg: '$heading800',
  flexDirection: 'row',
});

const SuggestionTextContainer = styled(View)({
  flex: 1,
});

const SuggestionText = styled(BaseText)({
  color: '$background0',
  variant: 'text.small',
});

const SuggestionTextTouchable = styled(TextTouchable)({
  color: '$background0',
  variant: 'text.small',
  fontWeight: 'semi',
});

const LinkBlock = styled(Touchable)({
  height: 32,
  width: 62,
  borderRadius: 4,
  backgroundColor: '$background30',
  alignItems: 'center',
  justifyContent: 'center',
  px: '$3',
});

const DetailsText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  fontWeight: 'semi',
});

const StatusText = styled(BaseText)((props: IStatusStylesProps) => ({
  color: statusTextLocalColorMap[props.status],
  variant: 'text.medium',
  fontWeight: 'semi',
}));
const LinkText = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
  fontWeight: 'semi',
});

const DateText = styled(BaseText)({
  color: '$heading800',
  variant: 'text.medium',
  fontWeight: 'semi',
});

type NavigationPropType =
  TabNavigatorNavigationPropType<'TrackFeedback'>;

export interface ITrackFeedbackItemProps {
  item: TrackFeedback;
}

export const TrackFeedbackItem = observer(
  ({ item }: ITrackFeedbackItemProps) => {
    const { t } = useTranslation();
    const nav = useNavigation<NavigationPropType>();
    const modal = useModal<ModalStackParamsType>();
    const [popoverRef, popover] = usePopover();

    function copyLink() {
      ShareService.shareUrl(
        { url: item.trackLink },
        { clipboardMessage: t('trackFeedback.linkCopied') },
      );
    }

    function navigateToContactUs() {
      if (IS_WEB) {
        popover.closePopover();
      } else {
        modal.closeModal();
      }

      nav.navigate(screens.ProfileNavigator, {
        screen: screens.ContactUs,
      });
    }

    function showSuggestion() {
      if (IS_WEB) {
        popover.togglePopover();
      } else {
        modal.openModal(modals.ConfirmationModal, {
          title: t('trackFeedback.unableToReviewSuggestionTitle'),
          subtitle: t(
            'trackFeedback.unableToReviewSuggestionDescription',
          ),
          buttons: [
            {
              text: t(
                'trackFeedback.unableToReviewSuggestionContactUs',
              ),
              style: ButtonStyleEnum.Flat,
            },
            {
              text: t('trackFeedback.unableToReviewSuggestionCancel'),
            },
          ],
          onConfirm: () => {
            navigateToContactUs();
          },
        });
      }
    }

    const isUnableToReview =
      item.status === TrackFeedbackStatusEnum.UnableToReview;

    return (
      <Container>
        <View>
          <HelpingBlock>
            <DetailsText>{t('trackFeedback.date')}</DetailsText>
          </HelpingBlock>
          <Separator size="$4" />
          <HelpingBlock>
            <DetailsText>{t('trackFeedback.status')}</DetailsText>
          </HelpingBlock>
          <Separator size="$4" />
          <HelpingBlock>
            <DetailsText>{t('trackFeedback.track')}</DetailsText>
          </HelpingBlock>
        </View>
        <Separator vertical size="$6" />
        <View>
          <HelpingBlock>
            <DateText>{item.formattedTime}</DateText>
          </HelpingBlock>
          <Separator size="$4" />
          <StatusBlock status={item.status}>
            <StatusText status={item.status}>
              {t('trackFeedback.status', {
                context: statusLocalKeyMap[item.status],
              })}
            </StatusText>
            {isUnableToReview && (
              <Fragment>
                <Separator size="$4" vertical />
                <Popover
                  ref={popoverRef}
                  position={PopoverPositionEnum.Top}
                  offset={14}
                >
                  <IconTouchable
                    name="info-circle-light"
                    onPress={showSuggestion}
                    color={statusTextLocalColorMap[item.status]}
                  />
                  <SuggestionContainer>
                    <SuggestionTextContainer>
                      <SuggestionText>
                        {t(
                          'trackFeedback.unableToReviewSuggestionDescription',
                        )}
                      </SuggestionText>
                      <Separator size="$5" />
                      <SuggestionTextTouchable
                        onPress={navigateToContactUs}
                      >
                        {t(
                          'trackFeedback.unableToReviewSuggestionContactUs',
                        )}
                      </SuggestionTextTouchable>
                    </SuggestionTextContainer>
                    <Separator vertical size="$4" />
                    <IconTouchable
                      name="close-light"
                      color="$background0"
                      onPress={popover.closePopover}
                    />
                  </SuggestionContainer>
                </Popover>
              </Fragment>
            )}
          </StatusBlock>
          <Separator size="$4" />
          <LinkBlock onPress={copyLink}>
            <LinkText>
              <Icon color="$text400" size={16} name="link-light" />{' '}
              {t('trackFeedback.link')}
            </LinkText>
          </LinkBlock>
        </View>
      </Container>
    );
  },
);
